/**
 * Light-metering box editor: one frame from the device's newest recording, turned the way the
 * session player turns it, and a box the organizer drags over it. The box is kept in stored-frame
 * fractions (see lib/meteringBox.ts), which is what the phone meters on whichever way the viewer
 * turns the picture. The browser already applies the file's own rotation before the player's turn,
 * so the box maps through both.
 */

import Hls from 'hls.js';
import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { boxBetween, boxFromDrag, rawBoxToDisplay, type FractionPoint } from '@/lib/meteringBox';
import { composeRotations, parseMp4InitInfo, shownFileRotation, type Mp4InitInfo } from '@/lib/mp4InitInfo';
import { cn, extractErrorMessage, formatRelative } from '@/lib/utils';
import { toVideoRotation, type VideoRotation } from '@/lib/videoRotation';
import api from '../services/api';
import type { DeviceSession, MeteringBox } from '../types';
import { parseMasterManifest, resolveChildPlaylistUrl } from './viewer/resilientPlayback';

// A few seconds in, once the camera's auto-exposure has settled after the recording started.
const FRAME_AT_S = 3;
const LOAD_TIMEOUT_MS = 45_000;

/**
 * Media time to take the frame from: {@link FRAME_AT_S} into the first fragment that has video, or
 * the middle of one shorter than twice that, skipping gap fragments the phone has not uploaded
 * video for. Null when every fragment is a gap.
 */
export const stillFrameTime = (
  fragments: ReadonlyArray<{ start: number; duration: number; gap?: boolean }>,
): number | null => {
  const first = fragments.find((f) => !f.gap && f.duration > 0);
  return first ? first.start + Math.min(FRAME_AT_S, first.duration / 2) : null;
};

type FrameState = { kind: 'loading' } | { kind: 'ready' } | { kind: 'error'; message: string };

interface FramePickerProps {
  recording: DeviceSession;
  selection: MeteringBox | null;
  onSelect: (box: MeteringBox) => void;
}

const FramePicker: React.FC<FramePickerProps> = ({ recording, selection, onSelect }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [frame, setFrame] = useState<FrameState>({ kind: 'loading' });
  const [rotation, setRotation] = useState<VideoRotation>(0);
  const [initInfo, setInitInfo] = useState<Mp4InitInfo | null>(null);
  // Drawing waits for this: until the file's own rotation is known, a box can't be mapped.
  const [initChecked, setInitChecked] = useState(false);
  const [frameSize, setFrameSize] = useState<{ width: number; height: number } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [drag, setDrag] = useState<{ pointerId: number; from: FractionPoint; to: FractionPoint } | null>(
    null,
  );
  const sessionId = recording.session_id;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const controller = new AbortController();
    let settled = false;
    let hls: Hls | null = null;
    let target: number | null = null;
    let timeoutId: number | undefined;
    setFrame({ kind: 'loading' });
    setFrameSize(null);
    setInitInfo(null);
    setInitChecked(false);
    setDrag(null);

    const settle = (next: FrameState) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      // One frame is all this needs: stop fetching the rest of the recording.
      hls?.stopLoad();
      setFrame(next);
    };
    const fail = (message: string) => settle({ kind: 'error', message });
    timeoutId = window.setTimeout(() => fail('The recording took too long to load.'), LOAD_TIMEOUT_MS);

    const noteSize = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setFrameSize({ width: video.videoWidth, height: video.videoHeight });
      }
    };
    // hls.js seeks to the target itself once that fragment has buffered; until then the element
    // may already show the fragment's first frame, which is not the one asked for.
    const checkReady = () => {
      if (target === null || video.seeking || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
      if (Math.abs(video.currentTime - target) > 0.5) return;
      noteSize();
      settle({ kind: 'ready' });
    };
    const seekNative = () => {
      target =
        Number.isFinite(video.duration) && video.duration > 0
          ? Math.min(FRAME_AT_S, video.duration / 2)
          : FRAME_AT_S;
      video.currentTime = target;
    };
    const readyEvents = ['seeked', 'loadeddata', 'canplay'] as const;
    video.addEventListener('loadedmetadata', noteSize);
    video.addEventListener('resize', noteSize);
    readyEvents.forEach((name) => video.addEventListener(name, checkReady));

    // The video-only child, not the master: the master pairs it with the audio rendition, and the
    // element only shows a position where both have data, so an audio gap would hide the frame.
    const load = (videoUrl: string) => {
      if (Hls.isSupported()) {
        const instance = new Hls({
          // Nothing loads until the fragment list has picked a frame that has video.
          autoStartLoad: false,
          enableWorker: true,
          lowLatencyMode: false,
          // A recording still in progress is a live playlist: never pull toward its live edge.
          liveSyncDurationCount: 999,
          liveMaxLatencyDurationCount: 10000,
          maxBufferLength: 2,
          maxMaxBufferLength: 2,
        });
        hls = instance;
        instance.on(Hls.Events.LEVEL_LOADED, (_event, data) => {
          if (settled || target !== null) return;
          target = stillFrameTime(data.details.fragments);
          if (target === null) fail('No video from this recording has uploaded yet.');
          else instance.startLoad(target);
        });
        instance.on(Hls.Events.ERROR, (_event, data) => {
          if (data.details === Hls.ErrorDetails.LEVEL_EMPTY_ERROR) {
            fail('No video from this recording has uploaded yet.');
          } else if (data.fatal) {
            fail(`The recording didn't load (${data.details}).`);
          }
        });
        instance.loadSource(videoUrl);
        instance.attachMedia(video);
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS exposes no fragment list to skip gaps with, so it aims at the same offset.
        video.addEventListener('loadedmetadata', seekNative, { once: true });
        video.src = videoUrl;
      } else {
        fail("This browser can't play the recording.");
      }
    };

    // An unreadable init segment leaves the rotation unknown, which maps the box as if the file had none.
    const probeInit = async (videoUrl: string) => {
      try {
        const playlist = await fetch(videoUrl, { credentials: 'same-origin', signal: controller.signal });
        const map = playlist.ok ? /#EXT-X-MAP:[^\n]*URI="([^"]+)"/.exec(await playlist.text()) : null;
        if (map) {
          const initUrl = new URL(map[1], new URL(videoUrl, window.location.href)).toString();
          const init = await fetch(initUrl, { credentials: 'same-origin', signal: controller.signal });
          if (init.ok) setInitInfo(parseMp4InitInfo(new Uint8Array(await init.arrayBuffer())));
        }
      } catch {
        // Falls through to the unknown-rotation default.
      }
      if (!controller.signal.aborted) setInitChecked(true);
    };

    void (async () => {
      try {
        const coverage = await api.getSessionCoverage(sessionId);
        if (settled) return;
        if (coverage.media_contract_version !== 2) {
          fail('This recording is in a retired format and has no picture to show.');
          return;
        }
        setRotation(toVideoRotation(coverage.rotation));
        const masterUrl = `/api/fleet/sessions/${encodeURIComponent(sessionId)}/v2/master.m3u8`;
        const response = await fetch(masterUrl, {
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`The recording's playlist answered ${response.status}.`);
        const { videoUri } = parseMasterManifest(await response.text());
        if (settled) return;
        if (videoUri) {
          const videoUrl = resolveChildPlaylistUrl(masterUrl, videoUri);
          void probeInit(videoUrl);
          load(videoUrl);
        } else {
          fail('This recording has no video.');
        }
      } catch (err) {
        if (!controller.signal.aborted) fail(extractErrorMessage(err, 'The recording did not load.'));
      }
    })();

    return () => {
      settled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
      video.removeEventListener('loadedmetadata', noteSize);
      video.removeEventListener('loadedmetadata', seekNative);
      video.removeEventListener('resize', noteSize);
      readyEvents.forEach((name) => video.removeEventListener(name, checkReady));
      if (hls) {
        hls.destroy();
      } else if (video.hasAttribute('src')) {
        video.removeAttribute('src');
        video.load();
      }
    };
  }, [sessionId, attempt]);

  const stored = frameSize ?? { width: 16, height: 9 };
  const quarterTurn = rotation === 90 || rotation === 270;
  const shownWidth = quarterTurn ? stored.height : stored.width;
  const shownHeight = quarterTurn ? stored.width : stored.height;
  // The element keeps the stored frame's shape and turns about its centre into the picture box.
  // object-fill, not contain: the frame must cover the box exactly so the overlay's fractions are
  // the picture's, never a letterbox bar's.
  const videoStyle: React.CSSProperties = {
    width: quarterTurn ? `${(100 * shownHeight) / shownWidth}%` : '100%',
    height: quarterTurn ? `${(100 * shownWidth) / shownHeight}%` : '100%',
    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
  };

  // The stored frame reaches the screen turned by the file's rotation, then by the player's.
  const pictureRotation = composeRotations(shownFileRotation(initInfo, frameSize), rotation);
  const ready = frame.kind === 'ready' && initChecked;
  const shownBox = drag
    ? boxBetween(drag.from, drag.to)
    : selection
      ? rawBoxToDisplay(selection, pictureRotation)
      : null;

  const pointIn = (event: React.PointerEvent<HTMLDivElement>): FractionPoint | null => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  };

  return (
    <div className="space-y-2">
      <div
        className="relative mx-auto overflow-hidden rounded bg-bg-video"
        style={{
          aspectRatio: `${shownWidth} / ${shownHeight}`,
          width: `min(100%, calc(60vh * ${shownWidth / shownHeight}))`,
        }}
      >
        <video
          ref={videoRef}
          muted
          playsInline
          preload="auto"
          className="absolute left-1/2 top-1/2 max-w-none object-fill"
          style={videoStyle}
        />
        <div
          className={cn(
            'absolute inset-0 select-none',
            ready ? 'cursor-crosshair touch-none' : 'pointer-events-none',
          )}
          onPointerDown={(event) => {
            if (!ready || (event.pointerType === 'mouse' && event.button !== 0)) return;
            const point = pointIn(event);
            if (!point) return;
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            setDrag({ pointerId: event.pointerId, from: point, to: point });
          }}
          onPointerMove={(event) => {
            if (!drag || event.pointerId !== drag.pointerId) return;
            const point = pointIn(event);
            if (point) setDrag({ ...drag, to: point });
          }}
          onPointerUp={(event) => {
            if (!drag || event.pointerId !== drag.pointerId) return;
            const box = boxFromDrag(drag.from, pointIn(event) ?? drag.to, pictureRotation);
            setDrag(null);
            if (box) onSelect(box);
          }}
          onPointerCancel={(event) => {
            if (drag && event.pointerId === drag.pointerId) setDrag(null);
          }}
        >
          {ready && shownBox && (
            <div
              className="pointer-events-none absolute border-2 border-accent"
              style={{
                left: `${shownBox.x * 100}%`,
                top: `${shownBox.y * 100}%`,
                width: `${shownBox.w * 100}%`,
                height: `${shownBox.h * 100}%`,
                // Dims the rest of the picture; the picture box's overflow-hidden clips it.
                boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.45)',
              }}
            />
          )}
        </div>
        {frame.kind === 'loading' && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-white">
            <Spinner size={28} />
          </div>
        )}
        {frame.kind === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 p-4 text-center">
            <span className="text-xs text-white">{frame.message}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAttempt((n) => n + 1)}
              className="text-white"
            >
              <RefreshCw />
              Retry
            </Button>
          </div>
        )}
      </div>
      <p className="text-center font-mono text-[11px] text-fg-muted">
        Frame from {sessionId}
        {recording.started_at ? ` · ${formatRelative(recording.started_at)}` : ''}
      </p>
    </div>
  );
};

interface MeteringBoxDialogProps {
  open: boolean;
  onClose: () => void;
  /** The device's newest recording with media: null when it has none, undefined while loading. */
  recording: DeviceSession | null | undefined;
  /** The box being edited, in stored-frame fractions. */
  value: MeteringBox | null;
  /** "Use box" passes the drawn box, "Remove box" null. Nothing is saved from here. */
  onApply: (box: MeteringBox | null) => void;
}

const BoxEditor: React.FC<Omit<MeteringBoxDialogProps, 'open'>> = ({
  recording,
  value,
  onApply,
  onClose,
}) => {
  const [selection, setSelection] = useState<MeteringBox | null>(value);
  const apply = (box: MeteringBox | null) => {
    onApply(box);
    onClose();
  };

  return (
    <>
      {recording === undefined && (
        <div className="flex flex-col items-center gap-3 py-12">
          <Spinner size={28} />
          <span className="text-sm text-fg-secondary">looking for this phone&apos;s latest recording…</span>
        </div>
      )}
      {recording === null && (
        <p className="py-8 text-center text-sm text-fg-secondary">
          A box is drawn on a frame from this phone&apos;s footage, so it needs one recording first.
          Record once with the phone mounted where it will run, then come back.
        </p>
      )}
      {recording && (
        <FramePicker recording={recording} selection={selection} onSelect={setSelection} />
      )}
      <DialogFooter className="gap-2 sm:gap-0">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="outline"
          disabled={value === null && selection === null}
          onClick={() => apply(null)}
          className="border-border text-fg-secondary hover:border-status-error hover:text-status-error"
        >
          Remove box
        </Button>
        <Button
          variant="outline"
          disabled={selection === null}
          onClick={() => apply(selection)}
          className="border-accent-purple text-accent-purple hover:bg-accent-purple/10"
        >
          Use box
        </Button>
      </DialogFooter>
    </>
  );
};

export const MeteringBoxDialog: React.FC<MeteringBoxDialogProps> = ({
  open,
  onClose,
  recording,
  value,
  onApply,
}) => (
  <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
    <DialogContent className="max-h-[95vh] max-w-3xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-accent">Measure light from an area</DialogTitle>
        <DialogDescription>
          Drag a box over what must not blow out, usually the windshield: the phone sets its
          exposure from that area.
        </DialogDescription>
      </DialogHeader>
      {/* The editor's effects need its <video>, which only exists once the dialog's portal has
          rendered, so they live inside the content rather than on the dialog. */}
      <BoxEditor recording={recording} value={value} onApply={onApply} onClose={onClose} />
    </DialogContent>
  </Dialog>
);

export default MeteringBoxDialog;
